import { useState, useCallback, useEffect, useRef } from 'react';
import { useStreamChat } from '../../messaging/contexts/StreamChatContext';
import { apiCall } from '../../services/apiUtil';
import { resolveImageUrl } from '../../profile/hooks/useProfileImage';

export const useMarketplaceMessages = () => {
  const { chatClient, user: currentUser } = useStreamChat();
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  // Tracks in-flight markRead calls per channelId to prevent concurrent duplicates
  const markReadInFlight = useRef({});

  // Fetch all marketplace conversations for the current user
  const fetchConversations = useCallback(async () => {
    if (!chatClient || !currentUser) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Query all messaging channels where user is a member and channel has marketplace flag
      const filter = {
        type: 'messaging',
        members: { $in: [String(currentUser.id)] },
        marketplace: true,
      };

      const sort = [{ last_message_at: -1 }];

      const channels = await chatClient.queryChannels(filter, sort, {
        watch: true,
        state: true,
        limit: 50,
      });

      // Collect unique listing IDs and user IDs for batch fetch
      const listingIds = [...new Set(channels.map(c => c.data?.listingId).filter(Boolean))];
      const otherUserIds = [...new Set(channels.map(c => {
        const members = Object.values(c.state.members);
        const other = members.find(m => m.user_id !== String(currentUser.id));
        return other?.user?.id;
      }).filter(Boolean))];

      // Two batch calls in parallel instead of N×2 individual calls
      const [listingsResult, usersResult] = await Promise.all([
        listingIds.length > 0
          ? apiCall('/marketplace/listings/batch', { method: 'POST', body: JSON.stringify({ ids: listingIds }) }).catch(() => null)
          : Promise.resolve([]),
        otherUserIds.length > 0
          ? apiCall('/user/batch', { method: 'POST', body: JSON.stringify({ ids: otherUserIds }) }).catch(() => null)
          : Promise.resolve([]),
      ]);

      const listingsById = Object.fromEntries((listingsResult || []).map(l => [l.id, l]));
      const usersById = Object.fromEntries((usersResult || []).map(u => [u.id, u]));

      // Transform channels to conversation format
      const formattedConversations = channels.map((channel) => {
        const data = channel.data || {};
        const members = Object.values(channel.state.members);
        const otherMember = members.find(m => m.user_id !== String(currentUser.id));
        const otherUser = otherMember?.user || {};

        const lastMessage = channel.state.messages?.[channel.state.messages.length - 1];

        const listingData = listingsById[data.listingId] || null;
        const listingImageUrl = resolveImageUrl(listingData?.images?.[0]?.url || '');

        const otherUserData = usersById[otherUser.id] || null;
        const firstName = otherUserData?.firstName || (otherUser.name || '').split(' ')[0] || '';
        const lastName = otherUserData?.lastName || (otherUser.name || '').split(' ').slice(1).join(' ') || '';

        return {
          id: channel.id,
          cid: channel.cid,
          channel,
          listingId: data.listingId,
          listingTitle: listingData?.title || data.listingTitle || 'Listing',
          listingPrice: listingData?.price || data.listingPrice || 0,
          listingImage: listingImageUrl,
          sellerId: data.sellerId,
          buyerId: data.buyerId,
          otherUser: {
            id: otherUser.id,
            name: otherUser.name || `User ${otherUser.id}`,
            firstName,
            lastName,
            profileImage: otherUserData?.profileImage || null,
            image: otherUser.image,
            online: otherUser.online,
          },
          lastMessage: lastMessage ? {
            text: lastMessage.text,
            createdAt: lastMessage.created_at,
            userId: lastMessage.user?.id,
          } : null,
          unreadCount: channel.countUnread(),
          updatedAt: data.last_message_at || data.created_at,
        };
      });

      setConversations(formattedConversations);

      const totalUnread = formattedConversations.reduce((sum, conv) => sum + conv.unreadCount, 0);
      setUnreadCount(totalUnread);
    } catch (err) {
      console.error('Error fetching marketplace conversations:', err);
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [chatClient, currentUser]);

  // Mark a conversation as read
  const markAsRead = useCallback(async (channelId) => {
    if (!chatClient) return;
    if (markReadInFlight.current[channelId]) return;
    markReadInFlight.current[channelId] = true;

    try {
      const channel = chatClient.channel('messaging', channelId);
      await channel.markRead();

      setConversations(prev => prev.map(conv =>
        conv.id === channelId ? { ...conv, unreadCount: 0 } : conv
      ));

      setUnreadCount(prev => {
        const conv = conversations.find(c => c.id === channelId);
        return Math.max(0, prev - (conv?.unreadCount || 0));
      });
    } catch (err) {
      console.error('Error marking marketplace message as read:', err);
    } finally {
      delete markReadInFlight.current[channelId];
    }
  }, [chatClient, conversations]);

  // Listen for new messages on marketplace channels
  useEffect(() => {
    if (!chatClient) return;

    let debounceTimer = null;

    // Increment unread count locally — no re-query on every message
    const handleNewMessage = (event) => {
      if (!event.channel_id?.startsWith('mp-')) return;
      const userId = String(currentUser?.id);
      if (event.message?.user?.id === userId) return;
      setConversations(prev => prev.map(conv =>
        conv.id === event.channel_id
          ? { ...conv, unreadCount: conv.unreadCount + 1 }
          : conv
      ));
      setUnreadCount(prev => prev + 1);
    };

    // After a read, debounce a single re-fetch to sync state
    const handleMarkRead = (event) => {
      if (!event.channel_id?.startsWith('mp-')) return;
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(fetchConversations, 2000);
    };

    chatClient.on('message.new', handleNewMessage);
    chatClient.on('message.read', handleMarkRead);

    return () => {
      clearTimeout(debounceTimer);
      chatClient.off('message.new', handleNewMessage);
      chatClient.off('message.read', handleMarkRead);
    };
  }, [chatClient, currentUser, fetchConversations]);

  // Initial load
  useEffect(() => {
    if (chatClient && currentUser) {
      fetchConversations();
    }
  }, [chatClient, currentUser, fetchConversations]);

  return {
    conversations,
    loading,
    error,
    unreadCount,
    fetchConversations,
    markAsRead,
  };
};

export default useMarketplaceMessages;
